import {type ReactNode, type Ref} from 'react';

import {type XAxisFormatValues} from '../../generated/graphql';
import {type ColorPaletteOption} from '../../util/colors';
import {type LegendPosition} from '../../util/plotHelpers/legendTypes';
import {type PlotFontSizeOrAuto} from '../../util/plotHelpers/plotFontSize';
import {type PlotType} from '../../util/plotHelpers/types';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {
  type AnnotationHoverSource,
  type AnnotationItem,
} from '../vis/annotations';
import {type ChartData} from '../vis/chartSeries';
import {type GraphInteraction} from '../vis/interaction';
import {
  type DerivedPointVisualizationOption,
  type ExcludeOutliersValues,
  type SinglePointRenderMode,
  type TooltipNumberOfRunsOptions,
} from '../WorkspaceDrawer/Settings/types';
import {type GraphZoom as PreparedGraphZoom} from './zoom/getGraphZoom';

/** Annotation display and actions. Hover changes notify only subscribing readers. */
export type GraphAnnotations = {
  activeCount: number;
  isActive: boolean;
  isEnabled: boolean;
  items: AnnotationItem[];
  markerQueryXMax?: number | null;
  markerQueryXMin?: number | null;
  onActivate?: (id: string) => void;
  onHover?: (id: string | null) => void;
  readHoveredId: AnnotationHoverSource['get'];
  subscribeToHover: AnnotationHoverSource['subscribe'];
};

export type GraphAppearance = {
  fontSize: PlotFontSizeOrAuto;
  isDarkMode: boolean;
};

/** Resolved coordinate semantics; the saved diagnostic key belongs to errors. */
export type GraphAxis = {
  outlierPolicy: ExcludeOutliersValues | undefined;
  xIsDatetime: boolean;
  xKey: string;
  xScale: 'linear' | 'log';
  xTitle?: string;
  yScale: 'linear' | 'log';
  yTitle?: string;
};

export type GraphErrors = {
  /** True for an expression entry, including an empty editor entry. */
  hasExpressions: boolean;
  hasUnexpectedError?: boolean;
  metricKeys?: string[];
  savedXKey?: string;
};

export type GraphFallbacks = {
  barYMax?: number | null;
  barYMin?: number | null;
  singlePointStyle?: SinglePointRenderMode;
};

export type GraphLayout = {sizeRef: Ref<HTMLDivElement>};

export type GraphLegendLinkTarget = {legendHref?: string};

export const DEFAULT_GRAPH_LEGEND_POSITION: LegendPosition = 'north';

/** Omission disables the legend. Visibility still respects available panel space. */
export type GraphLegendFeature = {
  colorPalette?: ColorPaletteOption;
  colorRunNames?: boolean;
  isSingleRun: boolean;
  position?: LegendPosition;
  resolveRunLink?: (target: GraphLegendLinkTarget) => string | undefined;
  show?: boolean;
};

export type GraphLoading = {
  isActive: boolean;
  isReady: boolean;
  /** Null disables the mounted regex-edit grace window. */
  regexSettlingKey: string | null;
};

/** Independent flat warning families, preserving each count's denominator. */
export type GraphNotices = {
  /** Presence enables limit notices; zero remains a valid limit. */
  displayRowLimitPerRunset?: number;
  fetchedRunCount?: number;
  groupedIncludedRunCount?: number;
  groupInputRunLimitPerRunset?: number;
  groupingDepth?: number;
  hasGroupedRunsets?: boolean;
  hasSubsampledGroups?: boolean;
  isDelayed?: true;
  /** Presence enables sampling notices, including false with an empty warning list. */
  isSamplingFallback?: boolean;
  maxFetchedRunsPerRunset?: number;
  metricCount?: number;
  onDelayedNoticeShown?: () => void;
  onLoadAnyway?: () => void;
  runsetCount?: number;
  samplingWarnings?: string[];
  showLimitHelp?: boolean;
};

/** Shared mounted identity and metric mode, independent of optional features. */
export type GraphPanel = {id: string; isMultiMetric: boolean};

/** Requested point strategy and effective fidelity remain separate facts. */
export type GraphRendering = {
  allowUPlot: boolean;
  exportMode: boolean;
  forceReactVis: boolean;
  isFullFidelity: boolean;
  plotType: PlotType | undefined;
  pointStrategy: DerivedPointVisualizationOption;
};

/** Presence displays a header. Empty text remains a present header. */
export type GraphTitle = {
  onActivate?: () => void;
  renderHighlightedText?: (text: string) => ReactNode;
  text: string;
};

/** Scalar run identity passed to host navigation policy. */
export type GraphRunLinkTarget = {
  runName: string;
  entityName?: string;
  projectName?: string;
  isGrouped?: boolean;
};

/** Tooltip values, formatting and dedicated host actions. */
export type GraphTooltip = {
  colorPalette: ColorPaletteOption;
  colorRunNames?: boolean;
  highlightedCompanionRunOnly?: boolean;
  onActivateRunLink?: (href: string) => void;
  /** Displayed X coordinate; the host owns axis-specific effects. */
  onSnapToX?: (x: number) => void;
  resolveRunLink?: (target: GraphRunLinkTarget) => string | undefined;
  runNameTruncation?: TruncationType;
  runVisibility?: TooltipNumberOfRunsOptions;
  showFullRunName?: boolean;
  showSnapToXHint?: boolean;
  showXRange?: boolean;
  xValueFormat?: XAxisFormatValues;
};

export type GraphZoom = {
  isActive: boolean;
  isZooming: boolean;
  /** Explicit query limits, including zero, independent of displayed domains. */
  isZoomedXMax: boolean;
  isZoomedXMin: boolean;
  onReset?: () => void;
  onZoom: PreparedGraphZoom['onZoom'];
  xDomain: PreparedGraphZoom['xDomain'];
  yDomain: PreparedGraphZoom['yDomain'];
};

/** One level of purpose groups. Retain qualified reads such as axis.xKey. */
export type GraphRenderProps = {
  annotations?: GraphAnnotations;
  appearance: GraphAppearance;
  axis: GraphAxis;
  errors: GraphErrors;
  fallbacks?: GraphFallbacks;
  /** Supply matching readers and writers together; omission uses chart-local state. */
  interaction?: GraphInteraction;
  layout: GraphLayout;
  legend?: GraphLegendFeature;
  loading: GraphLoading;
  notices?: GraphNotices;
  panel: GraphPanel;
  rendering: GraphRendering;
  series: ChartData['series'];
  seriesMetadataById: ChartData['seriesMetadataById'];
  seriesStylesById: ChartData['seriesStylesById'];
  title?: GraphTitle;
  tooltip?: GraphTooltip;
  zoom: GraphZoom;
};
